import { KpiMetricCard } from "./KpiMetricCard";
import { WidgetPlaceholder } from "./WidgetPlaceholder";
import { SpendRoasTrendChart } from "./charts/SpendRoasTrendChart";
import { PurchasesByDayChart } from "./charts/PurchasesByDayChart";
import { RevenueTrendChart } from "./charts/RevenueTrendChart";
import { SpendPacingChart } from "./charts/SpendPacingChart";
import { PlacementBreakdownChart } from "./charts/PlacementBreakdownChart";
import { DeviceBreakdownChart } from "./charts/DeviceBreakdownChart";
import { AgeGenderHeatmap } from "./charts/AgeGenderHeatmap";
import { CreativeFormatChart } from "./charts/CreativeFormatChart";
import { DaypartingHeatmap } from "./charts/DaypartingHeatmap";
import { PlatformSpendChart } from "./charts/PlatformSpendChart";
import { PlatformRoasChart } from "./charts/PlatformRoasChart";
import { CrossPlatformTrendChart } from "./charts/CrossPlatformTrendChart";
import { ConversionFunnelChart } from "./charts/ConversionFunnelChart";
import { VideoPerformanceChart } from "./charts/VideoPerformanceChart";
import { PlatformComparisonChart } from "./charts/PlatformComparisonChart";
import { BlendedRoasCard } from "./cards/BlendedRoasCard";
import { TikTokEngagementCard } from "./cards/TikTokEngagementCard";
import { TopCampaignsTable } from "./tables/TopCampaignsTable";
import { TopAdsTable } from "./tables/TopAdsTable";
import { UnifiedTopAdsTable } from "./tables/UnifiedTopAdsTable";
import { CreativeFatigueTable } from "./tables/CreativeFatigueTable";
import { AttributionComparisonTable } from "./tables/AttributionComparisonTable";
import { QualityScoresTable } from "./tables/QualityScoresTable";
import { GeoBreakdownTable } from "./tables/GeoBreakdownTable";

interface WidgetRendererProps {
  widgetId: string;
  title: string;
  gridH?: number;
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

type WidgetComponent = React.FC<{
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}>;

const CHART_MAP: Record<string, WidgetComponent> = {
  "chart-spend-roas-trend": SpendRoasTrendChart,
  "chart-purchases-by-day": PurchasesByDayChart,
  "chart-revenue-trend": RevenueTrendChart,
  "chart-spend-pacing": SpendPacingChart,
  "chart-placement": PlacementBreakdownChart,
  "chart-device": DeviceBreakdownChart,
  "chart-age-gender": AgeGenderHeatmap,
  "chart-creative-format": CreativeFormatChart,
  "chart-dayparting": DaypartingHeatmap,
  "chart-platform-spend": PlatformSpendChart,
  "chart-platform-roas": PlatformRoasChart,
  "chart-cross-platform-trend": CrossPlatformTrendChart,
  "chart-conversion-funnel": ConversionFunnelChart,
  "chart-video-performance": VideoPerformanceChart,
  "chart-platform-comparison": PlatformComparisonChart,
};

const TABLE_MAP: Record<string, WidgetComponent> = {
  "table-campaigns": TopCampaignsTable,
  "table-top-ads": TopAdsTable,
  "table-unified-top-ads": UnifiedTopAdsTable,
  "table-creative-fatigue": CreativeFatigueTable,
  "table-attribution": AttributionComparisonTable,
  "table-quality-scores": QualityScoresTable,
  "table-geo": GeoBreakdownTable,
};

const CARD_MAP: Record<string, WidgetComponent> = {
  "card-blended-roas": BlendedRoasCard,
  "card-tiktok-engagement": TikTokEngagementCard,
};

export function WidgetRenderer({ widgetId, title, gridH, isEditMode, onRemove, onResize, onResetSize }: WidgetRendererProps) {
  if (widgetId.startsWith("kpi-")) {
    return (
      <KpiMetricCard
        widgetId={widgetId}
        gridH={gridH}
        isEditMode={isEditMode}
        onRemove={onRemove}
        onResize={onResize}
        onResetSize={onResetSize}
      />
    );
  }

  const CardComponent = CARD_MAP[widgetId];
  if (CardComponent) {
    return (
      <CardComponent
        isEditMode={isEditMode}
        onRemove={onRemove}
        onResize={onResize}
        onResetSize={onResetSize}
      />
    );
  }

  const ChartComponent = CHART_MAP[widgetId];
  if (ChartComponent) {
    return (
      <div style={{ overflow: "hidden", minWidth: 0, width: "100%", height: "100%" }}>
        <ChartComponent
          isEditMode={isEditMode}
          onRemove={onRemove}
          onResize={onResize}
          onResetSize={onResetSize}
        />
      </div>
    );
  }

  const TableComponent = TABLE_MAP[widgetId];
  if (TableComponent) {
    return (
      <div style={{ overflow: "hidden", minWidth: 0, width: "100%", height: "100%" }}>
        <TableComponent
          isEditMode={isEditMode}
          onRemove={onRemove}
          onResize={onResize}
          onResetSize={onResetSize}
        />
      </div>
    );
  }

  return (
    <WidgetPlaceholder
      widgetId={widgetId}
      title={title}
      isEditMode={isEditMode}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
    />
  );
}
